import { NetworkStatus, useReactiveVar } from '@apollo/client';
import {
  CatalogStorefront,
  ProductCatalogAttributeSelectionType,
  ProductCatalogSearchQueryFiltersSelectionGroupInput,
  ProductCatalogSearchQueryInput,
  ProductCatalogSearchQueryOrderBy
} from '@graphql/generated';
import { useLocation } from '@react-router';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import produce from 'immer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { PagesForTelemetry } from '../..';
import { SORT_BY_CODE, activeFiltersVar } from '../../Catalog.cache';
import { CatalogProductListFilters } from '../../Catalog.types';
import { CatalogDepartmentDictionaryEntry } from '../../state/Catalog';
import { VALUE_PROP_CARDS_PAGES_COUNT as VALUE_PROP_CARDS_PAGES_COUNT, VALUE_PROP_CARDS_PER_PAGE } from '../../components/ProductTile/components/ValuePropCard/constants';
import { getSelectedPriceRangeFromString } from '../../utils/priceRange';
import { BUCKETED_CUSTOM_CODE } from '../../components/RefinementFacet/components/RefinementFacetPriceBuckets/RefinementFacetPriceBuckets.controller';
import { useTrackCatalogPageByUrl } from '../useTrackCatalogPageViaLocationState';
import { BABY_SHOP_PATH, CATALOG_PATH } from '../../Catalog.constants';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import { useCatalogLoggingContext } from '../../Catalog.logging';
import { useSearchProductsProvider } from '../../routes/CatalogProductsIndex/components/SearchProductsProvider/SearchProductsProvider';

type UseCatalogProductListArgs = {
  /**
   * When not provided, will default to `pageSize`
   */
  initialPageSize?: number;
  pageSize: number;
  telemetryPageValue: PagesForTelemetry;
  selectedCategory: Maybe<CatalogDepartmentDictionaryEntry>;
  searchTerm: Maybe<string>;
  skip?: boolean;
  url?: string;
  showValuePropCards?: boolean;
};

const queryUrl = (currentStorefront: CatalogStorefront) => (currentStorefront === CatalogStorefront.baby ? BABY_SHOP_PATH : CATALOG_PATH);

const usePrimaryCatalogData = ({ initialPageSize, pageSize, searchTerm, telemetryPageValue, skip, url, showValuePropCards }: UseCatalogProductListArgs) => {
  const location = useLocation();
  const trackCatalogPageByUrl = useTrackCatalogPageByUrl();
  const { currentStorefront } = useShopStorefrontContext();
  const { trackLoadMoreProductsButtonClicked } = useCatalogLoggingContext();

  const filtersApiDataRef = useRef<CatalogProductListFilters>([]);
  const activeFiltersMap = useReactiveVar(activeFiltersVar);

  const queryTextOrURl = searchTerm || url || location.pathname;
  useEffect(() => {
    filtersApiDataRef.current = [];
  }, [queryTextOrURl]);

  const sortBy = useRef<ProductCatalogSearchQueryOrderBy>();
  const isCustomPriceBucketActive = useRef<boolean>(false);

  // Compose `filterSelectionGroups` query input arg from the activeFilters reactive var
  const composedFilterSelectionGroupInput = useMemo(() => {
    const groupedActiveFilters: Record<string, string[]> = {};
    isCustomPriceBucketActive.current = false;
    sortBy.current = undefined;
    Object.keys(activeFiltersMap).forEach(compositeCode => {
      if (activeFiltersMap[compositeCode]) {
        switch (compositeCode) {
          case ProductCatalogAttributeSelectionType.priceRange:
            /**
             * If it's priceRange, then get the value from activeFiltersMap corresponding value
             * In this case value should be a range string <number>-<number>
             * Also set isCustomPriceBucketActive to true amd add priceBucket:custom to activeFiltersMap
             */
            const currentActiveFiltersCompositeCode = activeFiltersMap[compositeCode] as string;
            groupedActiveFilters[compositeCode] = [currentActiveFiltersCompositeCode];
            activeFiltersMap[BUCKETED_CUSTOM_CODE] = true;
            isCustomPriceBucketActive.current = true;
            break;

          case SORT_BY_CODE:
            sortBy.current = activeFiltersMap[compositeCode] as ProductCatalogSearchQueryOrderBy;
            break;

          case BUCKETED_CUSTOM_CODE:
            /**
             * If compositeCode is priceBucket:custom, then ignore it in the query
             * but keep it's value in a ref
             * This case is meant to handle when the user clicks on `Custom` price bucket
             * but no price range is selected
             */
            isCustomPriceBucketActive.current = activeFiltersMap[compositeCode] as boolean;
            break;

          default:
            /**
             * If it's not priceRange, priceBucket:custom or sortBy, then get the value from splitting compositeCode
             */
            const [groupCode, valueCode] = compositeCode.split(':');
            const groupValues = groupedActiveFilters[groupCode] || [];
            groupValues.push(valueCode);
            groupedActiveFilters[groupCode] = groupValues;
        }
      }
    });

    const stringListFilters = Object.keys(groupedActiveFilters).reduce((acc, groupCode) => {
      switch (groupCode) {
        case ProductCatalogAttributeSelectionType.priceRange:
          const currentValue = groupedActiveFilters[groupCode][0];
          const priceRange = getSelectedPriceRangeFromString(currentValue);

          /**
           * If it's priceRange, then find if priceRange is already in the acc
           * If priceRange is not in the acc, then create and push a new filter
           * If priceRange is in the acc, then update the current priceRange
           */
          const priceRangeIndex = acc.findIndex(filter => filter.groupCode === ProductCatalogAttributeSelectionType.priceRange);
          if (priceRangeIndex === -1) {
            const newFilter = {
              groupCode: ProductCatalogAttributeSelectionType.priceRange,
              priceRange
            };
            acc.push(newFilter);
          } else {
            acc[priceRangeIndex].priceRange = priceRange;
          }
          return acc;

        case SORT_BY_CODE:
          return acc;

        default:
          /**
           * If it's not priceRange, then push the new filter
           */
          acc.push({ groupCode, valueCodes: groupedActiveFilters[groupCode] });
          return acc;
      }
    }, [] as Array<ProductCatalogSearchQueryFiltersSelectionGroupInput>);

    return stringListFilters;
  }, [activeFiltersMap]);

  const productCatalogSearchQueryInput: ProductCatalogSearchQueryInput = searchTerm
    ? {
        url: queryUrl(currentStorefront),
        filters: {
          queryText: queryTextOrURl,
          filterSelectionGroups: composedFilterSelectionGroupInput
        },
        orderBy: sortBy.current ?? null
      }
    : {
        url: queryTextOrURl || null,
        filters: {
          filterSelectionGroups: composedFilterSelectionGroupInput
        },
        orderBy: sortBy.current ?? null
      };

  const { useGetGridItemsQuery } = useSearchProductsProvider();
  const { data, loading, error, fetchMore, networkStatus, productList } = useGetGridItemsQuery({
    batchMode: 'fast',
    // https://github.com/apollographql/apollo-client/issues/6916#issuecomment-895581411
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip,
    variables: {
      query: productCatalogSearchQueryInput,
      first: initialPageSize ?? pageSize,
      withFilters: true
    }
  });

  const pageInfo = data?.productUniversalSearchResults?.pagedResults?.pageInfo;

  // We update the filters ref whenever the query has resolved
  // We're explicitly doing it here as opposed to `onCompleted` or in a `useEffect`
  // because `filtersApiDataRef` is mutable and doesn't trigger rerender.
  if (networkStatus === NetworkStatus.ready) {
    const allFilters = data?.productUniversalSearchResults?.allFilters?.filter(filterGroup => {
      if (filterGroup.attributeGroup.filterSelectionType !== ProductCatalogAttributeSelectionType.stringListMulti) return true;
      return filterGroup.attributes.length > 1;
    });
    filtersApiDataRef.current = allFilters || [];
  }

  const storedFiltersData = filtersApiDataRef.current;
  // Although we do configure apollo read policies on the `isActive` field
  // we want to ensure that it is up to date here
  const syncedCachedFilters = useMemo(() => {
    const next = produce(storedFiltersData, draft => {
      draft.map(filterGroup => {
        /**
         * Set 'isActive' attribute local-only field
         */
        filterGroup.attributes.forEach(att => {
          const currentValue = activeFiltersMap[att.compositeCode];
          switch (typeof currentValue) {
            case 'boolean':
              att.isActive = currentValue;
              break;
            case 'string':
              att.isActive = true;
              break;
            default:
              att.isActive = false;
          }
        });

        if (filterGroup.attributeGroup.filterSelectionType === ProductCatalogAttributeSelectionType.priceBucket) {
          const currentCustomIndex = filterGroup.attributes.findIndex(att => att.compositeCode === BUCKETED_CUSTOM_CODE);
          if (currentCustomIndex === -1) {
            filterGroup.attributes.push({
              compositeCode: BUCKETED_CUSTOM_CODE,
              isActive: isCustomPriceBucketActive.current,
              value: {
                code: 'custom',
                title: 'Custom'
              }
            });
          }
        }

        /**
         * Set 'selectedPriceRange' local-only fields
         */
        if (filterGroup.attributeGroup.filterSelectionType === ProductCatalogAttributeSelectionType.priceRange) {
          const currentValue = activeFiltersMap[ProductCatalogAttributeSelectionType.priceRange];
          filterGroup['selectedPriceRange'] = typeof currentValue === 'string' ? getSelectedPriceRangeFromString(currentValue) : null;
        }
      });
    });
    filtersApiDataRef.current = next;
    return next;
  }, [activeFiltersMap, storedFiltersData]);

  const handleOnLoadMoreProductsButtonClick = useEventCallback(() => {
    if (pageInfo?.hasNextPage) {
      trackLoadMoreProductsButtonClicked({
        method: 'load more button',
        page: telemetryPageValue,
        requestSize: pageSize,
        productCount: productList.length,
        startCursor: pageInfo.startCursor,
        endCursor: pageInfo.endCursor
      });

      // If value prop cards are enabled, the initial page size will take into account how many cards we display in a page,
      // so when loading more products we need to reset the page size to the original value once all the cards have been displayed
      const currentPage = Math.ceil(productList.length / pageSize);
      fetchMore &&
        fetchMore({
          variables: {
            first: showValuePropCards ? (currentPage < VALUE_PROP_CARDS_PAGES_COUNT ? pageSize : pageSize + VALUE_PROP_CARDS_PER_PAGE) : pageSize,
            endCursor: pageInfo.endCursor
          }
        });

      const path = location.pathname + location.search;
      trackCatalogPageByUrl(path, currentPage + 1);
    }
  });
  return {
    data,
    error,
    filters: syncedCachedFilters,
    sortBy: sortBy.current,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage: !!pageInfo?.hasNextPage,
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
    loading,
    productList,
    totalCount: pageInfo?.totalCount || 0
  } as const;
};

export const useCatalogProductListData = (args: UseCatalogProductListArgs) => {
  const { data, error, filters, sortBy, isFetchingMore, loading, productList, handleOnLoadMoreProductsButtonClick, hasNextPage, totalCount } = usePrimaryCatalogData(args);
  const { useGetGridItemsLazyQuery } = useSearchProductsProvider();
  const { sendRequest, loading: loadingFallbackCatalog, error: fallbackCatalogDataError, fallbackProductList } = useGetGridItemsLazyQuery({
    batchMode: 'fast',
    notifyOnNetworkStatusChange: true
  });

  const { currentStorefront } = useShopStorefrontContext();
  const loadFallbackCatalogProducts = useCallback(() => {
    sendRequest({
      variables: {
        query: { url: queryUrl(currentStorefront) },
        first: args.pageSize,
        withFilters: false
      }
    });
  }, [args.pageSize, sendRequest, currentStorefront]);

  return {
    data,
    productList,
    totalProductCount: totalCount,
    filters: filters,
    sortBy,
    loading,
    loadingFallbackCatalog,
    fallbackCatalogProductList: fallbackProductList,
    error,
    fallbackCatalogDataError,
    handleOnLoadMoreProductsButtonClick,
    hasNextPage,
    isFetchingMore,
    loadFallbackCatalogProducts
  };
};
